import axios from 'axios';

// Rutas de auth: un 401 ahí nunca dispara refresh (evita loops).
const AUTH_URLS = ['/auth', '/auth/refresh', '/auth/logout'];

const isAuthUrl = (url) => {
  if (!url) return false;
  const path = String(url).split('?')[0];
  return AUTH_URLS.some((u) => path === u || path.endsWith(`/api${u}`));
};

/**
 * Pide un access token nuevo. Usa axios plano (NO la instancia `api`) para no
 * pasar por el interceptor de respuesta. `withCredentials` solo acá (la cookie
 * `sispro_refresh` tiene Path=/api/auth), no de forma global.
 */
export const requestRefreshToken = async (baseURL) => {
  const res = await axios.post(`${baseURL}/auth/refresh`, null, {
    withCredentials: true,
    timeout: 15000,
  });
  return res.data.token;
};

/**
 * Crea el handler de errores de respuesta con refresh de token.
 * @param {object} deps
 * @param {import('axios').AxiosInstance} deps.api instancia usada para reintentar
 * @param {() => void} deps.closeSession limpia la sesión local
 * @param {() => void} deps.redirectToLogin redirige a '/'
 * @param {(baseURL: string) => Promise<string>} [deps.refresh] inyectable para tests
 */
export const createAuthErrorHandler = ({
  api,
  closeSession,
  redirectToLogin,
  refresh = requestRefreshToken,
}) => {
  // Promesa única compartida: N requests con 401 concurrentes -> 1 solo refresh.
  let refreshPromise = null;

  const expireSession = () => {
    closeSession();
    redirectToLogin();
  };

  const getFreshToken = () => {
    if (!refreshPromise) {
      refreshPromise = refresh(api.defaults.baseURL).finally(() => {
        refreshPromise = null;
      });
    }
    return refreshPromise;
  };

  return async (err) => {
    const status = err?.response?.status;
    if (status !== 401) return Promise.reject(err);

    const config = err.config;
    if (!config || config._retry || isAuthUrl(config.url)) {
      expireSession();
      return Promise.reject(err);
    }

    let token;
    try {
      token = await getFreshToken();
    } catch {
      // Refresh fallido (sin cookie / vencida / red): comportamiento previo.
      expireSession();
      return Promise.reject(err);
    }

    // Mismo formato que authHttp().
    localStorage.setItem('token', JSON.stringify(token));
    config._retry = true;
    config.headers = config.headers || {};
    config.headers['authorization'] = `Bearer ${token}`;
    // Si el reintento vuelve a dar 401, este mismo handler lo ve con _retry y cierra sesión.
    return api.request(config);
  };
};
